import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Box, FormControl, InputLabel, Select, MenuItem, Typography, Paper, TextField, Button, Tooltip, Card, Alert } from '@mui/material';
import QuestionForm from './QuestionForm';
import ExistingQuestions from './ExistingQuestions';
import AmsReportModal from './AmsReportModal';
import ShareModal from './ShareModal';
import CreateAsanaTaskModal from './CreateAsanaTaskModal';
import { toast } from 'react-toastify';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from 'docx';
import StrategicPulse from './StrategicPulse';
import ClientProfile from './ClientProfile';
import IndustryInsightsSummaryCard from './IndustryInsightsSummaryCard';

const QuestionsSection = ({ clientId, clientIsActive, clients, user, userData, selectedClientId, onClientSelect, onQuestionSubmitted, userAsanaConnection, onNavigateToTab, onOpenWizard }) => {
const [currentContextClientId, setCurrentContextClientId] = useState(null);
const [selectedClientIsActive, setSelectedClientIsActive] = useState(true);
const [isAmsModalOpen, setIsAmsModalOpen] = useState(false);
const [amsReportData, setAmsReportData] = useState(null);  
const [isAmsShareModalOpen, setIsAmsShareModalOpen] = useState(false);
const [isAmsAsanaModalOpen, setIsAmsAsanaModalOpen] = useState(false);
  // State and handler for the AMS Report Generator
const [amsDateFrom, setAmsDateFrom] = useState('');
const [amsDateTo, setAmsDateTo] = useState('');
const [generatingReport, setGeneratingReport] = useState(false);
const [forwardedSearchQuery, setForwardedSearchQuery] = useState(null);
const AMS_MAX_DATE_RANGE_DAYS = 92;
// Prevent one-time-setup screen from flashing while clients are still loading
const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);

useEffect(() => {
  // If clients is not yet an array, we're still loading → never show onboarding
  if (!Array.isArray(clients)) {
    setShouldShowOnboarding(false);
    return;
  }

  // If clients are present, hide immediately (no debounce) to prevent flash
  if (clients.length > 0) {
    setShouldShowOnboarding(false);
    return;
  }

  // Only if confirmed empty after a short delay, show onboarding
  const timer = setTimeout(() => setShouldShowOnboarding(true), 350);
  return () => clearTimeout(timer);
}, [clients]);
const asanaContextForModal = useMemo(() => {
    const client = clients?.find(c => c.id === currentContextClientId);
    if (client?.asanaConnection?.status === 'connected') {
      return { type: 'client', id: client.id };
    }
    if (userAsanaConnection?.status === 'connected') {
      return { type: 'user' };
    }
    return null; // No connection available
}, [clients, currentContextClientId, userAsanaConnection]);

const datesMatchLastReport = useMemo(() => {
    return amsReportData &&
           amsDateFrom === amsReportData.metadata?.dateRange?.from &&
           amsDateTo === amsReportData.metadata?.dateRange?.to;
}, [amsReportData, amsDateFrom, amsDateTo]);

const dateRangeInfo = useMemo(() => {
    if (!amsDateFrom || !amsDateTo) return { isValid: true, days: 0 };
    const from = new Date(amsDateFrom);
    const to = new Date(amsDateTo);
    if (from > to) return { isValid: false, days: 0 }; // Already handled by disabled logic, but good practice
    
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to be inclusive
    
    return {
        isValid: diffDays <= AMS_MAX_DATE_RANGE_DAYS,
        days: diffDays
    };
}, [amsDateFrom, amsDateTo]);

const handleInsightLinkClick = (insightId) => {
  toast.info(`Filtering feed for source: ${insightId.substring(0, 8)}...`);
  setForwardedSearchQuery({ id: insightId, timestamp: Date.now() });
  // Modal stays open - user can click multiple links or close manually
};
const handleGenerateAms = async () => {
  // Validate dates
  if (new Date(amsDateFrom) > new Date(amsDateTo)) {
    toast.error('Start date must be before end date');
    return;
  }
  
  setGeneratingReport(true);
  setIsAmsModalOpen(true); // Open modal immediately
  setAmsReportData(null); // Clear previous data
  
  try {
    const generateAMS = httpsCallable(functions, 'generateArchiveMasterSummary', {
  timeout: 450000  // 7.5 minutes in milliseconds
});
const result = await generateAMS({
  clientId: currentContextClientId,
  dateFrom: amsDateFrom,
  dateTo: amsDateTo
});
    
    setAmsReportData(result.data); // Set the report data for modal

// DO NOT reset dates. Keep them in sync with the generated report.
// setAmsDateFrom('');  <-- DELETE THIS LINE
// setAmsDateTo('');    <-- DELETE THIS LINE
  
} catch (error) {
  console.error('Error generating Chat Summary report:', error);
  toast.error(error.message || 'Failed to generate report');
  setIsAmsModalOpen(false);
} finally {
  setGeneratingReport(false);
}
};

// --- NEW HANDLERS FOR CHAT SUMMARY (AMS) MODAL ACTIONS ---

// Helper function to parse bold markdown into TextRun children
const parseBoldText = (text) => {
  const children = [];
  const parts = text.split(/(\*\*.*?\*\*)/g);
  for (const part of parts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      children.push(new TextRun({ text: part.slice(2, -2), bold: true }));
    } else if (part) {
      children.push(new TextRun({ text: part }));
    }
  }
  return children;
};

// Helper function to parse markdown report text into docx paragraphs
const parseReportTextToDocx = (text) => {
  const paragraphs = [];
  const lines = text.split('\n');
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    // Headers (check longest first: ####, ###, ##, #)
    if (trimmed.startsWith('#### ')) {
      paragraphs.push(new Paragraph({ 
        children: parseBoldText(trimmed.substring(5)),
        heading: HeadingLevel.HEADING_4,
        spacing: { before: 180, after: 80 }
      }));
    } else if (trimmed.startsWith('### ')) {
      paragraphs.push(new Paragraph({ 
        children: parseBoldText(trimmed.substring(4)),
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 100 }
      }));
    } else if (trimmed.startsWith('## ')) {
      paragraphs.push(new Paragraph({ 
        children: parseBoldText(trimmed.substring(3)),
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 250, after: 120 }
      }));
    } else if (trimmed.startsWith('# ')) {
      paragraphs.push(new Paragraph({ 
        children: parseBoldText(trimmed.substring(2)),
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 300, after: 150 }
      }));
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      // Bullet point (handle both markdown and pre-converted bullets)
      const bulletText = trimmed.startsWith('- ') ? trimmed.substring(2) : trimmed.substring(2);
      paragraphs.push(new Paragraph({ 
        children: [new TextRun({ text: '• ' }), ...parseBoldText(bulletText)],
        spacing: { after: 50 },
        indent: { left: 360 }
      }));
    } else if (trimmed === '---') {
      paragraphs.push(new Paragraph({ 
        text: '________________________________',
        spacing: { before: 200, after: 200 }
      }));
    } else {
      // Regular paragraph - handle bold
      paragraphs.push(new Paragraph({ children: parseBoldText(trimmed), spacing: { after: 100 } }));
    }
  }
  
  return paragraphs;
};
// 1. Word Export Handler
const generateAmsWordDocument = async (data) => {
  const { metadata, reportText } = data;
  const doc = new Document({
    sections: [{
      children: [
        new Paragraph({ text: "Chat Summary", heading: HeadingLevel.TITLE }),
        new Paragraph({ text: `Client: ${metadata.clientName}`, spacing: { after: 200 } }),
        new Paragraph({ text: `Period: ${metadata.dateRange.from} to ${metadata.dateRange.to}`, spacing: { after: 400 } }),
         ...parseReportTextToDocx(reportText),
      ],
    }],
  });
  return await Packer.toBlob(doc);
};

const handleExportAmsReport = async (format) => {
  if (!amsReportData) {
    toast.error('No report data to export.');
    return;
  }
  try {
    const blob = await generateAmsWordDocument(amsReportData);
    const filename = `Chat_Summary_${amsReportData.metadata.clientName}_${amsReportData.metadata.dateRange.from}_to_${amsReportData.metadata.dateRange.to}.docx`;
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully!');
  } catch (error) {
    console.error("Word export failed:", error);
    toast.error("Failed to export Word document.");
  }
};

// 2. Share via Email Handler
const handleShareAms = async (sharePayload) => {
    // Reconstruct the payload to ensure it matches the backend's expectation
    const payloadForBackend = {
        recipientEmail: sharePayload.recipientEmail,
        personalMessage: sharePayload.personalMessage,
        reportData: amsReportData // Explicitly use the report data from state
    };

    try {
        const shareAmsReport = httpsCallable(functions, 'shareAmsReportByEmail');
        await shareAmsReport(payloadForBackend);
        toast.success("Chat Summary Report shared successfully!");
        return { success: true };
    } catch (error) {
        console.error("Chat Summary Share failed:", error);
        toast.error(`Failed to share report: ${error.message}`);
        return { success: false, error: error.message };
    }
};

// 3. Asana Task Handler
const handleOpenAmsAsanaModal = () => {
    setIsAmsAsanaModalOpen(true);
};

  console.log('🔍 QuestionsSection rendering with props:', {
    clientId,
    clientIsActive,
    clients: clients?.length || 0,
    user: user?.email || 'no user'
  });

  // Set default client when clients load
  useEffect(() => {
    console.log('[DEBUG] QuestionsSection useEffect - clients changed:', {
      clientsLength: clients?.length,
      currentContextClientId,
      clientId
    });
    if (!Array.isArray(clients)) return;
    if (clients && clients.length > 0 && !currentContextClientId) {
      // If we have a specific clientId prop, use it
      if (clientId) {
        console.log('[DEBUG] Using provided clientId:', clientId);
        setCurrentContextClientId(clientId);
        const client = clients.find(c => c.id === clientId);
        setSelectedClientIsActive(client?.isActive === true);


      } else {
        // Default to first client
        const firstClient = clients[0];
        console.log('[DEBUG] Setting default client to first client:', firstClient.id);
        setCurrentContextClientId(firstClient.id);
        setSelectedClientIsActive(firstClient.isActive === true);
      }
    }


    // Auto-select the client if there is only one
    if (clients && clients.length === 1) {
      const singleClient = clients[0];
      if (currentContextClientId !== singleClient.id) {
        console.log('[DEBUG] Auto-selecting the only client:', singleClient.id);
        setCurrentContextClientId(singleClient.id);
        setSelectedClientIsActive(singleClient.isActive === true);
      }
    }
  }, [clients, clientId]); // ✅ FIXED: Removed currentContextClientId

 // Handle client selection change
const handleClientChange = (event) => {
    const newClientId = event.target.value;
    console.log('[DEBUG] Client selection changed to:', newClientId);
    setCurrentContextClientId(newClientId);

    // --- FIX: Reset Chat Summary state when client changes ---
    setAmsReportData(null);
    setAmsDateFrom('');
    setAmsDateTo('');
    // --- END FIX ---

    if (newClientId === 'ALL') {
        setSelectedClientIsActive(true);
    } else {
        const selectedClient = clients?.find(c => c.id === newClientId);
        setSelectedClientIsActive(selectedClient?.isActive === true);
    }
};

  // If no clients loaded yet, show the new user setup instructions (debounced to avoid flash)
if (shouldShowOnboarding) {
    return (
        <Box p={3} sx={{ maxWidth: '600px', margin: 'auto', textAlign: 'center', mt: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Welcome to SavvaiEdge!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Let's set up your first client. This guided 3-step process takes just a few minutes — you'll provide company details, connect financial data, and answer a few strategic questions to unlock AI-powered insights.
            </Typography>

            <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => onOpenWizard(null)}
                sx={{ textTransform: 'none', px: 4, py: 1.5, fontSize: '1.1rem', mb: 4 }}
            >
                Set Up Your First Client
            </Button>

            <Box sx={{ textAlign: 'left' }}>
                <Typography
                    variant="body2"
                    sx={{
                        cursor: 'pointer',
                        color: 'text.secondary',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '&:hover': { color: 'primary.main' }
                    }}
                    onClick={(e) => {
                        const details = e.currentTarget.nextElementSibling;
                        if (details) details.style.display = details.style.display === 'none' ? 'block' : 'none';
                    }}
                >
                    ▸ How does SavvaiEdge protect privacy?
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mt: 1, backgroundColor: 'grey.50', display: 'none' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Our AI doesn't need to know your client's name to understand their business. Before any data is sent to AI models, we anonymize it by replacing identifying information with generic placeholders. The AI analyzes financial patterns, market trends, and strategic goals without ever knowing the identity behind the data. We then re-insert your client's name only within your private, secure portal.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Think of it like a doctor reading an X-ray — they can make a precise diagnosis without knowing the patient's name. Our AI works the same way with your business data.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}

 // Guard: while clients are loading (not an array yet), render nothing to avoid flash/errors
  if (!Array.isArray(clients)) {
    return null;
  }

  console.log('[DEBUG] Rendering QuestionsSection with currentContextClientId:', currentContextClientId);

  // Trial status calculations
  const isTrialing = userData?.stripeSubscriptionStatus === 'trialing';
  const trialActivated = userData?.trialActivated === true;
  const trialEnd = userData?.trialEnd;

  const getDateFromField = (field) => {
    if (!field) return null;
    if (typeof field.toDate === 'function') return field.toDate();
    if (field instanceof Date) return field;
    return new Date(field);
  };

  const trialDaysRemaining = (() => {
    const endDate = getDateFromField(trialEnd);
    if (!endDate) return null;
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  })();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Trial Status Banner */}
      {isTrialing && !trialActivated && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => onNavigateToTab(2)}
            >
              Activate Now
            </Button>
          }
        >
          {trialDaysRemaining !== null ? (
            <>🎉 <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> left in your free trial.</>
          ) : (
            <>🎉 You're on a free trial.</>
          )}
          {' '}Activate your subscription to ensure uninterrupted access.
        </Alert>
      )}

      {/* Trial Activated Confirmation */}
      {isTrialing && trialActivated && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Subscription activated! Your trial continues and you'll be charged automatically when it ends.
        </Alert>
      )}

      {/* Client Selection Dropdown */}
      <Box sx={{ mb: 3, mt: 2 }}>
    {clients.length > 1 ? (
        // If more than one client, show the interactive dropdown
        <FormControl fullWidth size="small">
            <InputLabel>Select Client</InputLabel>
            <Select
                value={currentContextClientId || ''}
                onChange={handleClientChange}
                label="Select Client"
            >
                <MenuItem value="ALL">All Clients</MenuItem>
                {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                        {client.displayName || client.name || 'Unnamed Client'}
                        {client.isActive === false && ' (Inactive)'}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    ) : (
        // If only one client, show a clean static display
        <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Client
            </Typography>
            <Typography variant="h6" component="p">
                {clients[0]?.displayName || clients[0]?.name || 'Unnamed Client'}
                {clients[0]?.isActive === false && ' (Inactive)'}
            </Typography>
        </Box>
    )}
</Box>

      {/* Two-Panel Layout */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
  <Grid container spacing={3} sx={{ height: '100%' }}>
    {/* Left Panel - Question Form & AMS Generator */}
    <Grid size={{ xs: 12, md: 4 }}>
      <Box sx={{ height: '100%' }}>
        <QuestionForm
          clientId={currentContextClientId}
          clientIsActive={selectedClientIsActive}
          clients={clients}
          user={user}
          onQuestionSubmitted={onQuestionSubmitted}
        />

        {/* NEW: Strategic Pulse Component */}
        <StrategicPulse
          clientId={currentContextClientId}
          currentNarrative={
            clients?.find(c => c.id === currentContextClientId)?.clientStrategicNarrative
          }
          onUpdate={() => {
            console.log('[DEBUG] Strategic narrative updated.');
          }}
          clientIsActive={selectedClientIsActive}
        />

        {/* --- AMS Report Generator UI --- */}
        <Card elevation={2} sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'grey.300' }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: '1.3rem' }}>
            Chat Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Comprehensive history report with insights and hyperlinks.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Date From"
              type="date"
              value={amsDateFrom}
              onChange={(e) => setAmsDateFrom(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={generatingReport}
            />
            <TextField
              label="Date To"
              type="date"
              value={amsDateTo}
              onChange={(e) => setAmsDateTo(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={generatingReport}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleGenerateAms}
              disabled={
                generatingReport ||
                !currentContextClientId ||
                currentContextClientId === 'ALL' ||
                !amsDateFrom ||
                !amsDateTo ||
                (amsDateFrom && amsDateTo && new Date(amsDateFrom) > new Date(amsDateTo)) ||
                !dateRangeInfo.isValid
              }
              sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem' }}
            >
              {generatingReport ? 'Generating...' : 'Generate Report'}
            </Button>

            {amsReportData && !generatingReport && (
              <Tooltip
                title={
                  datesMatchLastReport
                    ? 'View the last generated report'
                    : 'Date range has changed. Please generate a new report.'
                }
              >
                <span>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setIsAmsModalOpen(true)}
                    sx={{ whiteSpace: 'nowrap' }}
                    disabled={!datesMatchLastReport}
                  >
                    View Last Report
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {amsDateFrom && amsDateTo && new Date(amsDateFrom) > new Date(amsDateTo) ? (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Start date must be before end date
            </Typography>
          ) : (
            !dateRangeInfo.isValid && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {`Date range cannot exceed ${AMS_MAX_DATE_RANGE_DAYS} days. Current range is ${dateRangeInfo.days} days.`}
              </Typography>
            )
          )}
        </Card>
        {/* --- Client Profile Card --- */}
        <ClientProfile
          clientId={currentContextClientId}
          clientIsActive={selectedClientIsActive}
        />

        {/* --- Industry Insights Summary Card --- */}
        <IndustryInsightsSummaryCard />
      </Box>
    </Grid>

    {/* Right Panel - Existing Questions */}
    <Grid size={{ xs: 12, md: 8 }}>
      <Box sx={{ height: '100%' }}>
        {currentContextClientId ? (
          <ExistingQuestions
            clientId={currentContextClientId}
            clientIsActive={selectedClientIsActive}
            clients={clients}
            user={user}
            userAsanaConnection={userAsanaConnection}
            onQuestionUpdate={onQuestionSubmitted}
            forwardedSearchQuery={forwardedSearchQuery}
          />
        ) : (
          <Box p={3}>
            <Typography>Please select a client to view questions</Typography>
          </Box>
        )}
      </Box>
    </Grid>
  </Grid>
</Box>
      
      {/* AMS Report Modal */}
      <AmsReportModal
  open={isAmsModalOpen}
  onClose={() => setIsAmsModalOpen(false)}
  reportData={amsReportData}
  isLoading={generatingReport}
  onExport={handleExportAmsReport}
  onShare={() => setIsAmsShareModalOpen(true)}
  onAsana={handleOpenAmsAsanaModal}
  isAsanaAvailable={!!asanaContextForModal}
  onInsightLinkClick={handleInsightLinkClick}
/>

      {isAmsShareModalOpen && amsReportData && (
        <ShareModal
          open={isAmsShareModalOpen}
          onClose={() => setIsAmsShareModalOpen(false)}
          item={amsReportData}
          itemType="ams_report"
          onShare={handleShareAms}
          user={user}
        />
      )}

      {isAmsAsanaModalOpen && amsReportData && (
  <CreateAsanaTaskModal
    open={isAmsAsanaModalOpen}
    onClose={() => setIsAmsAsanaModalOpen(false)}
    item={{
      title: `Review Chat Summary for ${amsReportData.metadata.clientName}`,
      content: amsReportData.reportText,
      dataType: 'ams_report',
      clientId: currentContextClientId,
      asanaContext: asanaContextForModal
    }}
    connectionContext={asanaContextForModal}
  />
)}
    </Box>
  );
};

export default QuestionsSection;